import React, { useState } from 'react';
import { useApp, PageView } from '../../context/AppContext';
import {
  LayoutDashboard,
  WalletCards,
  CheckSquare,
  FileText,
  LayoutGrid,
  Target,
  Bookmark,
  Flame,
  CalendarDays,
  BarChart3,
  X,
  LogOut,
  ChevronRight,
  Settings,
} from 'lucide-react';

export const MobileNav: React.FC = () => {
  const { currentPage, setCurrentPage, logout, language, t } = useApp();
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const isBn = language === 'bn';

  const mainTabs: { page: PageView; label: string; icon: any }[] = isBn
    ? [
        { page: 'dashboard', label: 'হোম', icon: LayoutDashboard },
        { page: 'finance', label: 'হিসাব', icon: WalletCards },
        { page: 'notes', label: 'নোটস', icon: FileText },
        { page: 'tasks', label: 'টাস্ক', icon: CheckSquare },
        { page: 'reports', label: 'রিপোর্ট', icon: BarChart3 },
      ]
    : [
        { page: 'dashboard', label: 'Home', icon: LayoutDashboard },
        { page: 'finance', label: 'Finance', icon: WalletCards },
        { page: 'notes', label: 'Notes', icon: FileText },
        { page: 'tasks', label: 'Tasks', icon: CheckSquare },
        { page: 'reports', label: 'Reports', icon: BarChart3 },
      ];

  const moreMenuItems: { page: PageView; label: string; icon: any; color: string; desc: string }[] = isBn
    ? [
        { page: 'goals', label: 'গোল বা প্ল্যান', icon: Target, color: 'text-teal-500 bg-teal-500/10', desc: 'বাৎসরিক রোডম্যাপ ও লক্ষ্য' },
        { page: 'resources', label: 'রিসোর্স লিংক', icon: Bookmark, color: 'text-cyan-500 bg-cyan-500/10', desc: 'দরকারি ওয়েব লিংক ও বুকমার্ক' },
        { page: 'habits', label: 'হ্যাবিট ট্র্যাকার', icon: Flame, color: 'text-amber-500 bg-amber-500/10', desc: 'দৈনিক রুটিন ও স্ট্রিক' },
        { page: 'calendar', label: 'ক্যালেন্ডার ও শিডিউল', icon: CalendarDays, color: 'text-emerald-500 bg-emerald-500/10', desc: 'ইভেন্ট ও রিমাইন্ডার শিডিউল' },
        { page: 'settings', label: 'সেটিংস ও ব্যাকআপ', icon: Settings, color: 'text-violet-500 bg-violet-500/10', desc: 'ক্লাউড সিঙ্ক, অ্যাপ লক ও প্রিফারেন্স' },
      ]
    : [
        { page: 'goals', label: 'Goals & Plans', icon: Target, color: 'text-teal-500 bg-teal-500/10', desc: 'Yearly roadmap and milestones' },
        { page: 'resources', label: 'Resources & Links', icon: Bookmark, color: 'text-cyan-500 bg-cyan-500/10', desc: 'Bookmarks, docs & web links' },
        { page: 'habits', label: 'Habit Tracker', icon: Flame, color: 'text-amber-500 bg-amber-500/10', desc: 'Daily routines and streak logs' },
        { page: 'calendar', label: 'Calendar & Schedule', icon: CalendarDays, color: 'text-emerald-500 bg-emerald-500/10', desc: 'Upcoming events & note reminders' },
        { page: 'settings', label: 'Settings & Backup', icon: Settings, color: 'text-violet-500 bg-violet-500/10', desc: 'Cloud sync, app lock & preferences' },
      ];

  const handleNavigate = (page: PageView) => {
    setCurrentPage(page);
    setIsMoreOpen(false);
  };

  const isMoreActive = ['goals', 'resources', 'habits', 'calendar', 'settings'].includes(currentPage);


  return (
    <>
      {/* Native App Style Bottom Tab Bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-[#0b0f19]/95 backdrop-blur-xl border-t border-slate-200/80 dark:border-slate-800/80 pb-safe select-none shadow-2xl">
        <div className="flex items-center justify-around px-1.5 py-1.5 max-w-md mx-auto">
          {mainTabs.map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.page;

            return (
              <button
                key={item.page}
                onClick={() => handleNavigate(item.page)}
                className={`flex flex-col items-center justify-center py-1 px-2.5 rounded-2xl relative transition-all active:scale-95 ${
                  isActive
                    ? 'text-emerald-600 dark:text-emerald-400 font-extrabold'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <div
                  className={`p-1.5 rounded-xl transition-all ${
                    isActive ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : ''
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'stroke-[2.5]' : ''}`} />
                </div>
                <span className={`text-[10px] mt-0.5 font-bold tracking-tight truncate ${isBn ? 'font-bangla' : ''}`}>
                  {item.label}
                </span>
              </button>
            );
          })}

          {/* Others Trigger Button */}
          <button
            onClick={() => setIsMoreOpen(!isMoreOpen)}
            className={`flex flex-col items-center justify-center py-1 px-2.5 rounded-2xl relative transition-all active:scale-95 ${
              isMoreActive || isMoreOpen
                ? 'text-emerald-600 dark:text-emerald-400 font-extrabold'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <div
              className={`p-1.5 rounded-xl transition-all ${
                isMoreActive || isMoreOpen ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : ''
              }`}
            >
              <LayoutGrid className={`w-5 h-5 ${isMoreActive || isMoreOpen ? 'stroke-[2.5]' : ''}`} />
            </div>
            <span className={`text-[10px] mt-0.5 font-bold tracking-tight ${isBn ? 'font-bangla' : ''}`}>
              {isBn ? 'অন্যান্য' : 'Others'}
            </span>
          </button>
        </div>
      </nav>

      {/* Mobile Slide-Up More Menu Drawer */}
      {isMoreOpen && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsMoreOpen(false);
          }}
          className="lg:hidden fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex flex-col justify-end animate-fade-in"
        >
          <div className="bg-white dark:bg-[#111827] rounded-t-3xl border-t border-slate-200 dark:border-slate-800 p-5 shadow-2xl max-h-[85vh] overflow-y-auto space-y-4 animate-scale-in pb-safe">
            {/* Drawer Handle & Header */}
            <div className="flex flex-col items-center">
              <div className="w-12 h-1.5 rounded-full bg-slate-300 dark:bg-slate-700 mb-3" />
              <div className="w-full flex items-center justify-between">
                <div>
                  <h3 className={`text-base font-black text-slate-900 dark:text-white ${isBn ? 'font-bangla' : ''}`}>
                    {isBn ? 'অন্যান্য বিষয় ও ফিচার' : 'Others'}
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    {isBn ? 'আপনার প্রয়োজনীয় যেকোনো সেকশন নির্বাচন করুন' : 'Select any section you want to navigate'}
                  </p>
                </div>
                <button
                  onClick={() => setIsMoreOpen(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl bg-slate-100 dark:bg-slate-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Menu Items List */}
            <div className="space-y-2">
              {moreMenuItems.map((m) => {
                const Icon = m.icon;
                const isSelected = currentPage === m.page;

                return (
                  <button
                    key={m.page}
                    onClick={() => handleNavigate(m.page)}
                    className={`w-full p-3.5 rounded-2xl border text-left transition-all flex items-center justify-between gap-3 ${
                      isSelected
                        ? 'border-emerald-500/50 bg-emerald-500/10 shadow-xs'
                        : 'border-slate-200 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${m.color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <div className={`text-xs font-bold text-slate-900 dark:text-white ${isBn ? 'font-bangla' : ''}`}>
                          {m.label}
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                          {m.desc}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  </button>
                );
              })}
            </div>

            {/* Logout Action in Drawer */}
            <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
              <button
                onClick={() => {
                  setIsMoreOpen(false);
                  logout();
                }}
                className="w-full py-2.5 px-4 bg-rose-500/10 hover:bg-rose-500 text-rose-600 hover:text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>{isBn ? 'লগআউট' : 'Log Out'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
